import { observer } from "mobx-react-lite";
import { ReactElement } from "react";
import {
  BoxProps,
  Center,
  Flex,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItem,
  MenuList,
  Spacer,
} from "@chakra-ui/react";

import "./index.scss";
import { genBlock } from "@/utils/bem";
import { themeStore } from "@/stores/theme";
import getInputProps from "@/styles/inputProps";
import { ReactComponent as ArrowDownIcon } from "@/assets/icons/arrow-down.svg";
import Icon from "@/components/Icon";

export interface ISelect<T extends string> extends MenuButtonProps {
  icon?: ReactElement;
  value?: string;
  options: T[];
  iconProps?: BoxProps;
  optionConverter?: (option: T) => string;
  onOptionClick: (option: T) => void;
}

const block = genBlock("c-select");

function Select<T extends string>({
  icon,
  value,
  options,
  iconProps,
  optionConverter,
  onOptionClick,
  ...others
}: ISelect<T>) {
  const { selectColor, selectBgColor, selectHoverColor } = themeStore.styles;
  const checkedProps = (checked: boolean) => {
    if (checked) {
      return {
        color: "#2bfbc6",
      };
    }
    return {};
  };
  return (
    <Menu>
      <MenuButton
        cursor="pointer"
        variant="unstyled"
        textAlign="left"
        userSelect="none"
        {...(getInputProps() as MenuButtonProps)}
        {...others}
      >
        <Flex>
          {value ? value : null}
          {value ? <Spacer /> : null}
          <Center color={selectColor} fontSize="8px" {...(iconProps ?? {})}>
            {icon ?? <Icon svg={ArrowDownIcon} className={block()} fillbyCurrentColor />}
          </Center>
        </Flex>
      </MenuButton>
      {options.length ? (
        <MenuList
          color={selectColor}
          bgColor={selectBgColor}
          borderColor={selectBgColor}
          fontSize="12px"
          maxH="300px"
          zIndex={10}
        >
          {options.map((option) => {
            return (
              <MenuItem
                key={option}
                _hover={{
                  bgColor: selectHoverColor,
                }}
                _focus={{
                  bgColor: "transparent",
                }}
                {...checkedProps(value === (optionConverter ? optionConverter(option) : option))}
                onClick={() => onOptionClick(option)}
              >
                {optionConverter ? optionConverter(option) : option}
              </MenuItem>
            );
          })}
        </MenuList>
      ) : null}
    </Menu>
  );
}

export default observer(Select);
